import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { SproutCommonContent } from "../../../../modules/content/components/SproutCommonContent/SproutCommonContent";
import { SproutMedia } from "../../../../modules/content/components/SproutMedia/SproutMedia";
import { SproutRichText } from "../../../../modules/content/components/SproutRichText/SproutRichText";
import { SproutSection } from "../../../../modules/content/components/SproutSection/SproutSection";
import type { IFeaturedTestimonialBlockProps } from "./FeaturedTestimonialBlock.props";

export const FeaturedTestimonialBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  parentClass,
  spacing,
  testimonial,
}: IFeaturedTestimonialBlockProps) => {
  if (!eyebrow && !heading && !description && !testimonial?.quote) return null;
  const Link = getComponent("Link");

  return (
    <SproutSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <SproutCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        {testimonial ? (
          <figure className="sprout-dot-field sprout-paper-lift grid gap-4 rounded-[2rem_2rem_2rem_0.5rem] border-2 border-navy-ink p-5 text-navy-ink md:grid-cols-[minmax(18rem,0.8fr)_1.2fr] md:p-8">
            {testimonial.portrait ? (
              <SproutMedia
                aspectRatio="1:1"
                className="sprout-sky-lift -rotate-2 rounded-[50%_50%_1.5rem_1.5rem] border-[0.55rem] border-whiteboard"
                media={testimonial.portrait.image}
                sizes="(min-width: 768px) 35vw, 100vw"
              />
            ) : null}
            <div
              className={cn(
                "sprout-workbook-paper flex flex-col justify-center rounded-[1.5rem_1.5rem_1.5rem_0.35rem] border-2 border-navy-ink p-7 md:p-10 lg:p-12",
                !testimonial.portrait && "md:col-span-full",
              )}
            >
              <blockquote>
                <SproutRichText
                  className="font-sprout-display text-3xl font-bold leading-tight text-navy-ink md:text-5xl"
                  content={testimonial.quote}
                />
              </blockquote>
              <figcaption className="mt-8">
                <p className="font-sprout-mono text-sm font-medium">{testimonial.name}</p>
                {testimonial.attribution ? (
                  <p className="mt-2 font-sprout-mono text-sm text-graphite">
                    {testimonial.attribution}
                  </p>
                ) : null}
              </figcaption>
              {testimonial.link ? (
                <Link
                  aria-label={testimonial.link.accessibilityLabel || undefined}
                  className="mt-6 inline-flex min-h-11 items-center self-start font-sprout-mono text-sm font-extrabold text-primary underline decoration-2 underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={testimonial.link.href}
                  rel={testimonial.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={testimonial.link.openInNewTab ? "_blank" : undefined}
                >
                  {testimonial.link.label}
                </Link>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>
    </SproutSection>
  );
};

FeaturedTestimonialBlock.displayName = "Block::FeaturedTestimonialBlock";
